'use strict';

// Brand tokens lifted from webpayment's app/utils/dmtTheme.js so the
// widgets read as the same product as the merchant dashboard's own
// AEPS/DMT screens.
const BX = {
  primary: '#1142d4',
  primaryDark: '#0e35a8',
  primaryLight: '#4169e1',
  success: '#059669',
  success2: '#16a34a',
  successTint: '#f0fdf4',
  successBorder: '#bbf7d0',
  warning: '#d97706',
  warningTint: '#fffbeb',
  warningBorder: '#fde68a',
  error: '#dc2626',
  error2: '#ef4444',
  errorTint: '#fef2f2',
  errorBorder: '#fecaca',
  ink: '#0f172a',
  muted: '#64748b',
  faint: '#94a3b8',
  border: '#e6eaf1',
};

const STYLE_ID = 'bbx-widget-styles';

const CSS = `
.bbx-widget, .bbx-widget * { box-sizing: border-box; }
.bbx-widget {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  width: 100%; max-width: 420px; margin: 0 auto; background: #fff; color: ${BX.ink};
  border: 1px solid ${BX.border}; border-radius: 16px;
  box-shadow: 0 1px 6px rgba(15,23,42,.05);
  overflow-x: hidden;
  /* Long forms (multi-step onboarding, document upload, ...) must not drag
     the header out of view along with the fields — cap the card's own
     height and scroll only .bbx-body, so the title/step-progress/back
     button stay put no matter how tall a given screen's content is. */
  display: flex; flex-direction: column;
  max-height: min(640px, 88vh);
}
@media (max-width: 380px) {
  .bbx-widget { border-radius: 12px; max-height: 92vh; }
}
.bbx-mono { font-family: 'DM Mono', 'SFMono-Regular', monospace; }
.bbx-header {
  display: flex; align-items: flex-start; gap: 12px;
  flex-shrink: 0; padding: 20px 20px 16px;
}
@media (max-width: 380px) { .bbx-header { padding: 16px 16px 14px; } }
.bbx-back {
  flex-shrink: 0; margin-top: 2px; width: 32px; height: 32px; border-radius: 10px;
  border: none; background: rgba(17,66,212,.1); color: ${BX.primary}; cursor: pointer;
  display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700;
}
.bbx-header-text h1 { font-size: 17px; font-weight: 800; letter-spacing: -.2px; margin: 0; }
.bbx-header-text p { font-size: 12px; color: ${BX.muted}; margin: 2px 0 0; }
.bbx-body {
  animation: bbx-fade .22s ease;
  flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden;
  padding: 0 20px 20px;
}
@media (max-width: 380px) { .bbx-body { padding: 0 16px 16px; } }
@keyframes bbx-fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.bbx-card {
  border: 1px solid ${BX.border}; border-radius: 12px; padding: 16px 18px;
  box-shadow: 0 1px 6px rgba(15,23,42,.05); margin-bottom: 14px;
}
.bbx-field { margin-bottom: 14px; }
.bbx-field label { display: block; font-size: 12px; font-weight: 700; color: ${BX.muted}; margin-bottom: 6px; }
.bbx-input {
  width: 100%; padding: 11px 13px; font-size: 14px; border: 1.5px solid ${BX.border};
  border-radius: 10px; font-family: inherit; color: ${BX.ink}; outline: none; transition: border-color .15s;
}
.bbx-input:focus { border-color: ${BX.primary}; }
.bbx-input.bbx-input-error { border-color: ${BX.error}; }
.bbx-hint { font-size: 11px; color: ${BX.faint}; margin-top: 5px; }
.bbx-error-text { font-size: 12px; color: ${BX.error}; margin-top: 5px; font-weight: 600; }
.bbx-btn {
  width: 100%; padding: 13px; font-size: 15px; font-weight: 800; border: none; border-radius: 10px;
  background: ${BX.primary}; color: #fff; cursor: pointer; box-shadow: 0 8px 22px rgba(17,66,212,.28);
  transition: transform .1s, opacity .15s;
}
.bbx-btn:active { transform: scale(.98); }
.bbx-btn:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
.bbx-btn-secondary { background: #fff; color: ${BX.primary}; box-shadow: none; border: 1.5px solid ${BX.border}; }
.bbx-btn-row { display: flex; gap: 10px; }
.bbx-btn-row .bbx-btn { flex: 1; }
.bbx-select-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
.bbx-select-item {
  border: 1.5px solid ${BX.border}; border-radius: 12px; padding: 14px; cursor: pointer;
  text-align: center; background: #fff; transition: border-color .15s, background .15s;
}
.bbx-select-item:hover { border-color: ${BX.primary}; background: rgba(17,66,212,.04); }
.bbx-select-item.bbx-selected { border-color: ${BX.primary}; background: rgba(17,66,212,.08); }
.bbx-select-item .bbx-sel-icon { font-size: 22px; margin-bottom: 6px; }
.bbx-select-item .bbx-sel-label { font-size: 12.5px; font-weight: 700; }
.bbx-amount-hero { font-size: 30px; font-weight: 800; text-align: center; margin: 6px 0 18px; font-family: 'DM Mono', monospace; }
.bbx-row { display: flex; justify-content: space-between; gap: 12px; padding: 7px 0; border-bottom: 1px dashed ${BX.border}; }
.bbx-row:last-child { border-bottom: none; }
.bbx-row-k { font-size: 11.5px; color: ${BX.muted}; flex-shrink: 0; }
.bbx-row-v { font-size: 12.5px; font-weight: 700; text-align: right; }
.bbx-otp-boxes { display: flex; gap: 8px; justify-content: center; margin: 10px 0; }
.bbx-otp-boxes input {
  flex: 1 1 40px; max-width: 40px; height: 48px; min-width: 0; text-align: center; font-size: 19px; font-weight: 700;
  border: 1.5px solid ${BX.border}; border-radius: 10px; font-family: 'DM Mono', monospace;
}
.bbx-otp-boxes input:focus { border-color: ${BX.primary}; outline: none; }
.bbx-otp-timer { text-align: center; font-size: 12.5px; color: ${BX.muted}; margin-top: 10px; }
.bbx-otp-resend { color: ${BX.primary}; font-weight: 800; cursor: pointer; }
.bbx-banner {
  display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border-radius: 10px;
  font-size: 12.5px; margin-bottom: 14px; border: 1px solid;
}
.bbx-banner-error { background: ${BX.errorTint}; border-color: ${BX.errorBorder}; color: ${BX.error}; }
.bbx-banner-success { background: ${BX.successTint}; border-color: ${BX.successBorder}; color: ${BX.success}; }
.bbx-banner-warning { background: ${BX.warningTint}; border-color: ${BX.warningBorder}; color: ${BX.warning}; }
.bbx-status-icon {
  width: 68px; height: 68px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  margin: 8px auto 14px; font-size: 32px; color: #fff;
}
.bbx-status-title { font-size: 16px; font-weight: 800; text-align: center; margin: 0 0 4px; }
.bbx-status-sub { font-size: 12.5px; color: ${BX.muted}; text-align: center; margin: 0 0 16px; }
.bbx-loading-wrap { display: flex; flex-direction: column; align-items: center; padding: 40px 0; gap: 14px; }
.bbx-spinner {
  width: 34px; height: 34px; border-radius: 50%; border: 3px solid ${BX.border};
  border-top-color: ${BX.primary}; animation: bbx-spin .8s linear infinite;
}
@keyframes bbx-spin { to { transform: rotate(360deg); } }
.bbx-overlay {
  position: fixed; inset: 0; background: rgba(15,23,42,.75); display: flex; align-items: center;
  justify-content: center; z-index: 999999; flex-direction: column; color: #fff; gap: 14px;
}
.bbx-overlay .bbx-spinner { border-color: rgba(255,255,255,.25); border-top-color: #fff; width: 46px; height: 46px; }
.bbx-overlay p { font-size: 14px; font-weight: 700; margin: 0; }
.bbx-overlay small { color: rgba(255,255,255,.65); font-weight: 500; }
.bbx-muted { color: ${BX.muted}; font-size: 12.5px; }
.bbx-empty { text-align: center; padding: 30px 10px; color: ${BX.faint}; font-size: 12.5px; }
.bbx-ben-card {
  display: flex; align-items: center; gap: 12px; padding: 13px; border: 1.5px solid ${BX.border};
  border-radius: 12px; margin-bottom: 10px; cursor: pointer; transition: border-color .15s;
}
.bbx-ben-card:hover { border-color: ${BX.primary}; }
.bbx-ben-logo {
  width: 40px; height: 40px; border-radius: 11px; flex-shrink: 0; display: flex; align-items: center;
  justify-content: center; color: #fff; font-weight: 800; font-size: 11px; background: ${BX.primary};
}
.bbx-ben-name { font-size: 13px; font-weight: 800; }
.bbx-ben-sub { font-size: 11px; color: ${BX.muted}; margin-top: 2px; }
.bbx-qr-box { display: flex; justify-content: center; margin: 8px 0 16px; }
.bbx-qr-box img { width: 220px; max-width: 100%; height: auto; aspect-ratio: 1 / 1; border: 1px solid ${BX.border}; border-radius: 12px; padding: 10px; }
.bbx-link { color: ${BX.primary}; font-weight: 700; cursor: pointer; font-size: 12.5px; text-align: center; display: block; margin-top: 10px; }
`;

// Ported from webpayment's app/utils/dmtTheme.js.
const BANK_LOGO_COLORS = {
  HDFC: '#004c8f', SBI: '#22409a', SBIN: '#22409a', ICICI: '#b8202e', ICIC: '#b8202e',
  AXIS: '#7b1fa2', UTIB: '#7b1fa2', PNB: '#a71930', KOTAK: '#ed1c24', YES: '#003057',
};

function bankAccent(ifsc) {
  const key = Object.keys(BANK_LOGO_COLORS).find((k) => (ifsc || '').toUpperCase().startsWith(k));
  return BANK_LOGO_COLORS[key] || BX.primary;
}

function injectStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const fontLink = document.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;600;700;800&family=DM+Mono:wght@600;700&display=swap';
  document.head.appendChild(fontLink);

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

module.exports = { BX, injectStyles, bankAccent };
